/**
 * PKCE (Proof Key for Code Exchange) utilities
 * 
 * Implements RFC 7636 for OAuth 2.0 public clients.
 * Used for secure platform token acquisition without exposing credentials.
 */

/**
 * Generate a cryptographically random code verifier
 * 
 * @param length - Length of verifier (43-128 chars, default: 128)
 * @returns Base64url-encoded random string
 */
export function generateCodeVerifier(length: number = 128): string {
  if (length < 43 || length > 128) {
    throw new Error('Code verifier length must be between 43 and 128');
  }

  // Use crypto.getRandomValues in browser
  const randomValues = new Uint8Array(length);
  
  if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(randomValues);
  } else if (typeof crypto !== 'undefined' && (crypto as any).getRandomValues) {
    // React Native / modern environments
    (crypto as any).getRandomValues(randomValues);
  } else {
    throw new Error(
      'No secure random source available. ' +
      'crypto.getRandomValues is required for PKCE. ' +
      'For server-side token generation, use clientId + clientSecret instead.'
    );
  }

  // Convert to base64url
  return base64UrlEncode(randomValues);
}

/**
 * Generate code challenge from code verifier
 * 
 * @param verifier - Code verifier
 * @param method - Challenge method ('S256' or 'plain')
 * @returns Code challenge
 */
export async function generateCodeChallenge(
  verifier: string,
  method: 'S256' | 'plain' = 'S256'
): Promise<string> {
  if (method === 'plain') {
    return verifier;
  }

  // S256: SHA-256 hash of verifier, then base64url encode
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  
  let hash: ArrayBuffer;
  
  if (typeof window !== 'undefined' && window.crypto?.subtle) {
    // Browser
    hash = await window.crypto.subtle.digest('SHA-256', data);
  } else if (typeof crypto !== 'undefined' && (crypto as any).subtle) {
    // React Native / modern environments
    hash = await (crypto as any).subtle.digest('SHA-256', data);
  } else {
    throw new Error(
      'No SHA-256 implementation available. ' +
      'crypto.subtle.digest is required for PKCE.'
    );
  }

  return base64UrlEncode(new Uint8Array(hash));
}

/**
 * Base64url encode (RFC 4648)
 * Standard base64 with URL-safe characters: + → -, / → _, no padding (=)
 */
function base64UrlEncode(buffer: Uint8Array): string {
  // Convert to base64
  let base64 = '';
  
  if (typeof window !== 'undefined' && window.btoa) {
    // Browser
    const binary = String.fromCharCode(...Array.from(buffer));
    base64 = window.btoa(binary);
  } else if (typeof btoa !== 'undefined') {
    // Some environments expose btoa globally
    const binary = String.fromCharCode(...Array.from(buffer));
    base64 = btoa(binary);
  } else {
    throw new Error(
      'No base64 encoding available. ' +
      'window.btoa is required for PKCE.'
    );
  }

  // Convert to base64url
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Generate PKCE pair (verifier + challenge)
 * 
 * @returns Object with verifier and challenge
 */
export async function generatePKCEPair(): Promise<{
  verifier: string;
  challenge: string;
}> {
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier, 'S256');
  
  return { verifier, challenge };
}
